import { randomInt } from 'node:crypto';
import { generateId, type UIMessage } from 'ai';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import type { Document } from '@/lib/db/schema';
import { genSaltSync, hashSync } from 'bcrypt-ts';
import type { Transaction as PluggyTransaction } from 'pluggy-sdk';

// Import installment processing from utils
export {
  processInstallmentStats,
  type InstallmentStats,
  type BillStatusMaps,
} from '@/utils/installmentProcessor';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface ApplicationError extends Error {
  info: string;
  status: number;
}

export const fetcher = async (url: string) => {
  const res = await fetch(url);

  if (!res.ok) {
    const error = new Error(
      'An error occurred while fetching the data.',
    ) as ApplicationError;

    error.info = await res.json();
    error.status = res.status;

    throw error;
  }

  return res.json();
};

export function getLocalStorage(key: string) {
  if (typeof window !== 'undefined') {
    return JSON.parse(localStorage.getItem(key) || '[]');
  }
  return [];
}

export function generateUUID(): string {
  // Use the Web Crypto CSPRNG (CWE-338): Math.random() is predictable and
  // chat ids act as capacity secrets in public-chat URLs. `crypto` is the Web
  // Crypto global, available identically server-side (Node >=19 / Next.js) and
  // in the browser, so no import is needed. Output shape (v4 UUID) is unchanged.
  return crypto.randomUUID();
}

export function getMostRecentUserMessage(messages: Array<UIMessage>) {
  const userMessages = messages.filter((message) => message.role === 'user');
  return userMessages.at(-1);
}

export function getDocumentTimestampByIndex(
  documents: Array<Document>,
  index: number,
) {
  if (!documents) return new Date();
  if (index > documents.length) return new Date();

  return documents[index].createdAt;
}

export function getTrailingMessageId({
  messages,
}: {
  messages: Array<{ id: string }>;
}): string | null {
  const trailingMessage = messages.at(-1);

  if (!trailingMessage) return null;

  return trailingMessage.id;
}

export function generateDummyPassword() {
  const password = generateId();

  const salt = genSaltSync(10);
  const hash = hashSync(password, salt);

  return hash;
}

export function formatCurrency(amount: string | number, currencyCode = 'BRL') {
  const value = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currencyCode,
  }).format(value);
}

export function calculateBalance(transactions: any[]) {
  return transactions.reduce((total, transaction) => {
    const amount = Number.parseFloat(transaction.amount);
    return total + amount;
  }, 0);
}

export function categorizeTransactions(transactions: any[]) {
  const categories: Record<string, number> = {};

  transactions.forEach((transaction) => {
    const amount = Math.abs(Number.parseFloat(transaction.amount));
    if (categories[transaction.category]) {
      categories[transaction.category] += amount;
    } else {
      categories[transaction.category] = amount;
    }
  });
  return categories;
}

type Merchant = NonNullable<PluggyTransaction['merchant']>;

type PaymentParticipant = {
  name?: string;
  document?: string;
  taxNumber?: string;
  cpf?: string;
  cnpj?: string;
};

type PaymentData = NonNullable<PluggyTransaction['paymentData']> & {
  payer?: PaymentParticipant;
  receiver?: PaymentParticipant;
};

type TransactionCreditCardData = NonNullable<
  PluggyTransaction['creditCardMetadata']
> & {
  installmentNumber?: number;
  totalInstallments?: number;
  billId?: string;
};

type CleanPaymentData = {
  payer?: {
    name?: string;
    document?: string;
  };
  receiver?: {
    name?: string;
    document?: string;
  };
};

type AccountType = 'BANK' | 'CREDIT';

type AccountSubtype = 'SAVINGS_ACCOUNT' | 'CHECKING_ACCOUNT' | 'CREDIT_CARD';

type BankData = {
  transferNumber: string;
  closingBalance: number;
  automaticallyInvestedBalance: number;
};

type CreditCardStatus = 'ACTIVE' | 'BLOCKED' | 'CANCELLED';

type CreditData = {
  brand: 'MASTERCARD' | 'VISA' | 'ELO';
  level: string;
  status: CreditCardStatus;
  creditLimit: number;
  balanceDueDate: string;
  minimumPayment: number;
  balanceCloseDate: string | null;
  availableCreditLimit: number;
  balanceForeignCurrency: number | null;
};

type InstallmentGroupInfo = {
  description: string;
  totalAmount: number;
  totalInstallments: number;
  purchaseDate: string;
  isComplete: boolean;
  currentInstallment: number;
  paidInstallments: number;
};

type CleanCreditCardData = {
  brand?: 'MASTERCARD' | 'VISA' | 'ELO';
  level?: string;
  status?: CreditCardStatus;
  creditLimit?: number;
  balanceDueDate?: string;
  minimumPayment?: number;
  balanceCloseDate?: string | null;
  availableCreditLimit?: number;
  balanceForeignCurrency?: number | null;
  installmentNumber?: number;
  totalInstallments?: number;
  installmentGroup?: InstallmentGroupInfo;
  disaggregatedCreditLimits?: any;
};

type Account = {
  accountId: string;
  providerCode: string;
  accountName: string;
  accountType: AccountType;
  accountSubtype: AccountSubtype;
  accountBalance: number;
  accountCurrencyCode: string;
  accountMarketingName: string;
  bankData?: BankData;
  creditData?: CreditData;
};

type Transaction = {
  id?: string;
  description: string;
  category: string | null;
  currency_code: string;
  amount: number;
  balance: number | null;
  date: string;
  type: string;
  status?: 'POSTED' | 'PENDING';
  payment_data: PaymentData | CleanPaymentData;
  credit_card_data: TransactionCreditCardData;
  merchant?: Merchant;
  account_name: string;
  account_type: AccountType;
  account_subtype: AccountSubtype;
  account_marketing_name: string;
  account_credit_data?: CreditData;
};

type Instructions = {
  description: string;
  dataStructure: string;
};

interface ProcessedTransactionsWithInstructions {
  transactions: Transaction[];
  instructions: Instructions;
}

export function cleanPaymentData(transactions: Transaction[]) {
  if (!transactions) return [];

  return transactions.map((transaction) => {
    if (transaction.account_type === 'CREDIT' || !transaction.payment_data) {
      return transaction;
    }

    return {
      ...transaction,
      payment_data: {
        payer: transaction.payment_data.payer
          ? {
              name: transaction.payment_data.payer.name,
              document: transaction.payment_data.payer.document,
            }
          : undefined,
        receiver: transaction.payment_data.receiver
          ? {
              name: transaction.payment_data.receiver.name,
              document: transaction.payment_data.receiver.document,
            }
          : undefined,
      },
    };
  });
}

export function processTransactions(transactions: Transaction[]) {
  const cleanedTransactions = cleanPaymentData(transactions);
  const transactionsWithInstructions =
    addInstructionsToTransactions(cleanedTransactions);

  return transactionsWithInstructions;
}

export function addInstructionsToTransactions(
  transactions: Transaction[],
): ProcessedTransactionsWithInstructions {
  return {
    instructions: {
      description:
        'Sistema de processamento de transações financeiras com suporte a contas bancárias e cartões de crédito.',
      dataStructure:
        'Array de transações com informações de conta e dados específicos por tipo (bancário ou crédito).',
    },
    transactions,
  };
}

// Gera uma API key no formato sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
const API_KEY_PREFIX = 'sk-';
const API_KEY_CHARS =
  'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const API_KEY_LENGTH = 32;

/**
 * Generates a cryptographically secure API key.
 *
 * Uses `node:crypto.randomInt` (a CSPRNG) instead of `Math.random()` so keys
 * are not feasible to brute-force or guess even when an attacker can observe
 * creation timing or infer server PRNG state. The output format (`sk-` + 32
 * chars from `[a-zA-Z0-9]`, ~190 bits of entropy) is unchanged so existing
 * storage, lookup, and UI continue to work.
 *
 * @returns A new API key string prefixed with `sk-`.
 */
export function generateSecureApiKey(): string {
  let result = API_KEY_PREFIX;
  for (let i = 0; i < API_KEY_LENGTH; i++) {
    result += API_KEY_CHARS.charAt(randomInt(API_KEY_CHARS.length));
  }

  return result;
}

/**
 * Validates that a callback URL is safe to redirect to.
 * Only allows relative paths (starting with /) that don't contain protocol handlers.
 * Prevents open redirect vulnerabilities by blocking absolute URLs and protocol-relative URLs.
 *
 * @param url - The URL to validate
 * @param fallback - The fallback URL to return if validation fails (defaults to '/chat')
 * @returns The validated URL or the fallback
 */
export function getSafeRedirectUrl(
  url: string | null | undefined,
  fallback = '/chat',
): string {
  if (!url) {
    return fallback;
  }

  // Trim whitespace
  const trimmedUrl = url.trim();

  // Block empty strings
  if (!trimmedUrl) {
    return fallback;
  }

  // Block protocol-relative URLs (//example.com)
  if (trimmedUrl.startsWith('//')) {
    return fallback;
  }

  // Block dangerous protocols (javascript:, data:, etc.)
  // For http/https URLs, extract the pathname (safe — redirect is always relative to our own domain)
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmedUrl)) {
    try {
      const parsed = new URL(trimmedUrl);
      if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
        return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
      }
    } catch {
      // Invalid URL, fall through to fallback
    }
    return fallback;
  }

  // Only allow paths starting with /
  if (!trimmedUrl.startsWith('/')) {
    return fallback;
  }

  // Block URLs that could be interpreted as protocol-relative after encoding tricks
  // e.g., /\example.com or \/example.com
  if (/^\/[\\\/]/.test(trimmedUrl)) {
    return fallback;
  }

  return trimmedUrl;
}

// Helper function to validate UUID format
export function isValidUUID(uuid: string | null | undefined): boolean {
  if (!uuid || uuid === 'undefined' || uuid === 'null') {
    return false;
  }
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

/**
 * Strips control characters (including null bytes) from a string.
 * Removes characters with codepoint 0x00-0x08, 0x0B-0x0C, 0x0E-0x1F (all ASCII control chars except tab/LF/CR).
 * This prevents "invalid byte sequence for encoding" errors in PostgreSQL UTF8.
 *
 * @param str - Input string
 * @returns Sanitized string with control characters removed
 */
export function stripControlCharacters(str: string): string {
  // Remove null bytes (\0) and other control characters that Postgres rejects
  // Keeps: tab (0x09), line feed (0x0A), carriage return (0x0D)
  // biome-ignore lint/suspicious/noControlCharactersInRegex: strips DB-invalid ctrl chars (excl tab/LF/CR)
  return str.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
}

// Helper function to validate and sanitize userId input
export function validateUserId(userId: string | null | undefined): {
  isValid: boolean;
  error?: string;
} {
  if (!userId) {
    return { isValid: false, error: 'User ID is required' };
  }

  if (userId === 'undefined' || userId === 'null') {
    return { isValid: false, error: 'Invalid User ID format' };
  }

  if (!isValidUUID(userId)) {
    return { isValid: false, error: 'Invalid User ID format' };
  }

  return { isValid: true };
}
